import MuiMenuItem from '@mui/material/MenuItem';
import MuiPagination from '@mui/material/Pagination';
import MuiSelect from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { ListParams } from 'src/utils/useListSearchParams';

import { Stack } from './Stack';
interface SearchParamsPaginationProps {
  total?: number;
  itemsPerPage?: number;
  params: ListParams;
  perPageOptions?: number[];
  onPerPageChange?: (newPerPage: number) => void;
  updateParam: (k: string, v: any) => void;
  onChange?: (newPage: number) => void;
  sx?: SxProps;
}

export const SearchParamsPagination: React.FC<SearchParamsPaginationProps> = ({
  itemsPerPage,
  params,
  perPageOptions,
  onPerPageChange,
  updateParam,
  onChange,
  total = 1,
  sx,
}) => {
  const handleChange = (newPage) => {
    updateParam('page', String(newPage));
    onChange && onChange(newPage);
  };

  const handlePerPageChange = (newPerPage: number) => {
    updateParam('perPage', String(newPerPage));
    onPerPageChange && onPerPageChange(newPerPage);
  };

  return (
    <Pagination
      onChange={handleChange}
      page={params.page}
      total={total}
      itemsPerPage={params.perPage || itemsPerPage}
      perPageOptions={perPageOptions}
      onPerPageChange={handlePerPageChange}
      sx={sx}
    />
  );
};

interface PaginationProps {
  page: number;
  onChange: (newPage: number) => void;
  total?: number;
  itemsPerPage?: number;
  perPageOptions?: number[];
  onPerPageChange?: (newPerPage: number) => void;
  sx?: SxProps;
}
export const Pagination: React.FC<PaginationProps> = ({
  onChange,
  itemsPerPage = 20,
  page = 1,
  total = 1,
  perPageOptions = [10, 20, 30, 40, 50],
  onPerPageChange,
  sx,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'pagination' });
  const firstItem = (page - 1) * itemsPerPage + 1;
  const lastPossibleItem = (page - 1) * itemsPerPage + itemsPerPage;
  const actualLastItem = lastPossibleItem > total ? total : lastPossibleItem;

  return (
    <Stack
      direction={{ sm: 'column', md: 'row' }}
      alignItems="center"
      justifyContent="center"
      sx={[
        {
          position: 'relative',
          py: 2,
          width: '100%',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <MuiPagination
        count={Math.ceil(total / itemsPerPage)}
        page={page}
        onChange={(e, newPage) => onChange(newPage)}
        defaultPage={1}
        siblingCount={0}
      />
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          my: 2,
          mx: 3,
          gap: 2,
          right: '0px',
          position: { md: 'absolute' },
        }}
      >
        <MuiSelect
          value={itemsPerPage}
          onChange={(e) =>
            onPerPageChange && onPerPageChange(Number(e.target.value))
          }
          size="small"
        >
          {perPageOptions.map((option) => (
            <MuiMenuItem key={option} value={option}>
              {option}
            </MuiMenuItem>
          ))}
        </MuiSelect>
        <Typography variant="body2" color="text.secondary">
          {t('itemRange', { firstItem, lastItem: actualLastItem, total })}
        </Typography>
      </Stack>
    </Stack>
  );
};
